import React, {
    createContext,
    useContext,
    ReactNode,
    useMemo,
    useState,
    useCallback,
    useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { RootState } from '../redux/store';
import { useDispatch, useSelector } from 'react-redux';
import { types } from '../redux/types';
import {
    asyncGetIpOrigin,
    asyncPatchCloseActivityTimes,
    asyncPostActivityTime,
} from '../services/activityTimes.service';
import {
    asyncGetStatus,
    asyncGetUserLog,
    asyncPostAgent,
} from '../services/general.service';
import { asyncGetBlockPages } from '../services/blockPages.service';
import { asyncGetParamChannels } from '../services/params.service';
import { asyncAuthRefresh } from '../services/auth.service';

interface Props {
    children?: ReactNode;
}

export const TOKEN = 'My_Token';
const AGENTID = 'AgentId_Log';
const EXPIRATION_TOKEN = 'Expiration_Token';
const GEOLOCATATION = 'Geolocation_Log';
const CHANNEL = 'Channel_Log';

let valuesTimes: {
    latitude: number;
    longitude: number;
    startTime: Date;
    endTime: Date;
    agentId: string;
    statusId: string;
    ip: string;
};

const extractList = (body: unknown): any[] | null => {
    if (Array.isArray(body)) {
        return body;
    }
    if (body && typeof body === 'object' && Array.isArray((body as any).items)) {
        const items = (body as any).items;
        return Array.isArray(items.items) ? items.items : items;
    }
    return null;
};

export const AuthContext = createContext<any>(null);

export default function AuthContextProvider({ children }: Props) {
    const dispatch: any = useDispatch();
    const { token, user, channelLog, exp, expirationToken, geolocation } =
        useSelector((state: RootState) => state.auth);
    const { statusList, channelList, logUser, ipOrigin } = useSelector(
        (state: RootState) => state.general
    );
    const [tokenAuth, setTokenAuth] = useState<any>(
        sessionStorage.getItem(TOKEN)
    );
    const [agentIdLog, setAgentIdLog] = useState<any>(
        localStorage.getItem(AGENTID)
    );
    const [dateExpirationToken, setDateExpirationToken] = useState<any | null>(
        localStorage.getItem(EXPIRATION_TOKEN)
    );
    const [coords, setCoords] = useState<any>(
        localStorage.getItem(GEOLOCATATION)
    );
    const [channel, setChannel] = useState<any>(localStorage.getItem(CHANNEL));
    const [loadingBlockPages, setLoadingBlockPages] = useState<boolean>(true);
    const [refreshingToken, setRefreshingToken] = useState<boolean>(false);
    const [lastRefreshAt, setLastRefreshAt] = useState<number | null>(null);

    const statusInitial = async (data: any) => {
        const noReady = data.filter((s: any) => s.name === 'Offline') || [];

        const body: any = await asyncPostAgent({ userId: user['id'] });
        if (body.items !== undefined) {
            localStorage.setItem(AGENTID, body.items.id);
            setAgentIdLog(body.items.id);
            if (data.length > 0) {
                valuesTimes = {
                    latitude: geolocation[1],
                    longitude: geolocation[0],
                    startTime: new Date(),
                    endTime: new Date(),
                    agentId: body.items.id,
                    statusId: noReady[0].id,
                    ip: ipOrigin,
                };

                if (valuesTimes.ip == '') {
                    asyncGetIpOrigin().then((body: any) => {
                        if (body) {
                            valuesTimes.ip =
                                body.ip != undefined ? body.ip : body.IPv4;
                        }
                        asyncPostActivityTime(valuesTimes);
                    });
                } else {
                    asyncPostActivityTime(valuesTimes);
                }
            }
        }
    };

    const logout = useCallback(function () {
        setTokenAuth('');
        setAgentIdLog('');
        setDateExpirationToken('');
        setCoords('');
        setChannel('');
        sessionStorage.removeItem(TOKEN);
        localStorage.clear();
        sessionStorage.clear();
    }, []);

    useEffect(() => {
        if (token) {
            let data: any = [];
            sessionStorage.setItem(TOKEN, token);
            setTokenAuth(token);
            localStorage.setItem(GEOLOCATATION, JSON.stringify(geolocation));
            setCoords(JSON.stringify(geolocation));
            localStorage.setItem(EXPIRATION_TOKEN, exp);
            setDateExpirationToken(exp);
            localStorage.setItem(CHANNEL, channelLog);
            setChannel(channelLog);
            if (statusList.length === 0) {
                asyncGetIpOrigin().then((body: any) => {
                    if (body) {
                        dispatch({
                            type: types.uploadIpOrigin,
                            payload: body.ip != undefined ? body.ip : body.IPv4,
                        });
                    }
                });
                asyncGetStatus()
                    .then((body: any) => {
                        const statuses = extractList(body);
                        if (statuses) {
                            data = statuses;
                            dispatch({
                                type: types.uploadStatus,
                                payload: statuses,
                            });
                        }
                    })
                    .finally(() => {
                        if (tokenAuth === '') {
                            statusInitial(data);
                        }
                    });
            }
        }

        if (logUser === undefined) {
            if (loadingBlockPages) {
                asyncGetBlockPages()
                    .then((body: any) => {
                        const pages = extractList(body);
                        if (pages) {
                            dispatch({
                                type: types.blockPages,
                                payload: pages,
                            });
                        }
                    })
                    .finally(() => setLoadingBlockPages(false));
            }
            if (token || tokenAuth) {
                asyncGetUserLog().then((body: any) => {
                    if (body.items !== undefined) {
                        dispatch({
                            type: types.uploadUserLog,
                            payload: body.items[0],
                        });
                    }
                });
                asyncGetStatus().then((body: any) => {
                    const statuses = extractList(body);
                    if (statuses) {
                        dispatch({
                            type: types.uploadStatus,
                            payload: statuses,
                        });
                    }
                });
                asyncGetIpOrigin().then((body: any) => {
                    if (body) {
                        dispatch({
                            type: types.uploadIpOrigin,
                            payload: body.ip != undefined ? body.ip : body.IPv4,
                        });
                    }
                });
            }
        }

        if (channelList.length === 0) {
            asyncGetParamChannels().then((body: any) => {
                const channels = extractList(body);
                if (channels) {
                    dispatch({
                        type: types.uploadChannels,
                        payload: channels,
                    });
                }
            });
        }
    }, [
        dispatch,
        token,
        statusList,
        tokenAuth,
        user,
        exp,
        statusInitial,
        logout,
        logUser,
        agentIdLog,
        expirationToken,
        geolocation,
        coords,
        ipOrigin,
        loadingBlockPages,
        channelLog,
        channel,
    ]);

    useEffect(() => {
        if (dateExpirationToken !== null && dateExpirationToken !== '') {
            const timer = window.setInterval(() => {
                if (
                    expirationToken !== true &&
                    dateExpirationToken <= new Date()
                ) {
                    // No cerrar actividad por expiración de token.
                    // Solo marcar expiración para redirigir al flujo correspondiente.
                    dispatch({
                        type: types.validationToken,
                    });
                }
            }, 6000);
            return () => {
                window.clearInterval(timer);
            };
        }
    }, [expirationToken, dateExpirationToken, dispatch]);

    // Auto-refresh del token cerca de la expiración (umbral dinámico)
    useEffect(() => {
        if (!tokenAuth || !exp) return;

        const checkAndRefresh = async () => {
            try {
                const now = new Date().getTime();
                const expTime = new Date(exp as any).getTime();
                const ttlMs = expTime - now;
                const longThresholdMs = 5 * 60 * 1000;
                const shortThresholdMs = 90 * 1000;
                const thresholdMs =
                    ttlMs > longThresholdMs
                        ? longThresholdMs
                        : shortThresholdMs;
                const minCooldownMs = 60 * 1000; // evitar bucles de refresh

                if (
                    !refreshingToken &&
                    ttlMs > 0 &&
                    ttlMs <= thresholdMs &&
                    (!lastRefreshAt || now - lastRefreshAt >= minCooldownMs)
                ) {
                    console.log(
                        '[Auth] Intentando refresh de token. TTL(ms):',
                        ttlMs
                    );
                    setRefreshingToken(true);
                    const res: any = await asyncAuthRefresh();
                    if (res && res.token) {
                        // Actualizar almacenamiento y estado
                        sessionStorage.setItem(TOKEN, res.token);
                        dispatch({
                            type: types.authRefresh,
                            payload: { token: res.token },
                        });
                        setLastRefreshAt(now);
                        console.log('[Auth] Token renovado correctamente.');
                    }
                }
            } catch (e) {
                // Si falla el refresh, dejar el flujo de expiración existente actuar
                console.error('[Auth] Error al renovar el token', e);
            } finally {
                setRefreshingToken(false);
            }
        };

        const interval = window.setInterval(checkAndRefresh, 10000);
        return () => window.clearInterval(interval);
    }, [tokenAuth, exp, dispatch, refreshingToken, lastRefreshAt]);

    const value = useMemo(
        () => ({
            coords,
            tokenAuth,
            agentIdLog,
            channel,
            logout,
        }),
        [coords, tokenAuth, agentIdLog, channel, logout]
    );

    return (
        <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    );
}

AuthContextProvider.propTypes = {
    children: PropTypes.object,
};

export function useAuthContext() {
    return useContext(AuthContext);
}
